import React from 'react';
import { motion } from 'framer-motion';
import ScrollContainer from 'react-indiana-drag-scroll';
import MediaComponent from '../MediaComponent';
import PostStats from '../components/PostStats';
import CommentList from '../components/CommentList';
import CommentCard from '../components/CommentCard';
import { Brand, Post, VideoData, VideoRefs } from '../types/socialGlass';
import { UI_CONSTANTS } from '../constants/uiConstants';

interface BrandDetailViewProps {
  selectedBrand: Brand;
  topBrands: Brand[];
  brandPosts: Post[];
  brandComments: any[];
  brandSentiments: Record<string, string>;
  idToData: Record<string, VideoData>;
  videoRefs: React.MutableRefObject<VideoRefs>;
  handleManualMute: () => void;
  handleManualUnmute: () => void;
  onBrandSelect: (brand: Brand) => void;
  onBack: () => void;
  isBrandSelected: (brand: Brand) => boolean;
}

const BrandDetailView: React.FC<BrandDetailViewProps> = ({
  selectedBrand,
  topBrands,
  brandPosts,
  brandComments,
  brandSentiments,
  idToData,
  videoRefs,
  handleManualMute,
  handleManualUnmute,
  onBrandSelect,
  onBack,
  isBrandSelected,
}) => {
  return (
    <motion.div
      key="brand-details"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col gap-8 p-6"
      style={{ fontFamily: "Acumin Variable Concept, sans-serif" }}
    >
      {/* Back to summary */}
      <button
        className="mb-2 w-fit text-sm text-[#DB4225] underline hover:opacity-80"
        onClick={onBack}
      >
        Back to summary
      </button>

      {/* Top Brands (non-scrollable, highlight with consistent card style) */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Top Brands</h2>
        </div>
        <div className="flex flex-wrap justify-start gap-4 pb-2">
          {topBrands.map((brand, idx) => {
            const selected = isBrandSelected(brand);
            const iconSrc = selected
              ? "/brandIconPick.svg"
              : "/brandIcon.svg";
            const cardBg = selected ? UI_CONSTANTS.COLORS.PRIMARY_BG : "bg-white";

            return (
              <button
                key={idx}
                onClick={() => onBrandSelect(brand)}
                className={`flex min-w-[200px] max-w-[240px] flex-row items-center justify-center gap-2 rounded-2xl px-3 py-3 transition-all duration-150 focus:outline-none ${
                  selected
                    ? `border-none border-[#DB4225] ${cardBg} shadow-md`
                    : `border border-transparent bg-white shadow-socialglass hover:shadow-md`
                }`}
              >
                <img
                  src={iconSrc}
                  alt="brand icon"
                  className="h-10 w-10 rounded-full"
                />
                <div className="flex flex-col items-start justify-center gap-y-2 overflow-hidden text-left">
                  <div className="font-regular text-[12px] leading-tight text-gray-400">
                    {brand.mentions}{" "}
                    {brand.mentions === 1 ? "mention" : "mentions"}
                  </div>
                  <div className="truncate text-base font-bold leading-tight text-black">
                    {brand.name}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Overall Brand Sentiment */}
      <div>
        <h2 className="mb-2 text-left text-2xl font-bold">
          Overall Brand Sentiment
        </h2>
        <div className="pl-1 text-left text-base text-gray-700">
          {brandSentiments[selectedBrand.name] || "Missing"}
        </div>
      </div>

      {/* Brand Related Posts */}
      <div className="w-full">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold">Brand Related Posts</h2>
          <span className="text-xs text-gray-500">
            ({brandPosts.length} posts)
          </span>
        </div>
        <ScrollContainer className="hide-scrollbar flex h-full items-start gap-x-8 overflow-x-auto overflow-y-hidden active:cursor-grabbing">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.4 }}
            className="ml-6 flex h-[300px] w-full flex-none overflow-hidden rounded-xl bg-white shadow-lg"
          >
            {brandPosts.map((post, idx) => (
              <div key={idx} className="flex h-[300px] w-full flex-none overflow-hidden rounded-xl bg-white shadow-lg">
                {/* Left: Video/Image */}
                <div className="flex h-full w-[180px] flex-col items-center justify-center">
                  <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-black shadow">
                    {idToData[post.postId]?.downloaded_path ? (
                      <>
                        <MediaComponent
                          postId={post.postId}
                          username={post.postUsername || post.user.name}
                          index={idx}
                          idToData={idToData}
                          videoRefs={videoRefs}
                          handleManualMute={handleManualMute}
                          handleManualUnmute={handleManualUnmute}
                          muted={true}
                          caption=""
                          showIcon={true}
                          isAutoPlay={false}
                        />
                        <PostStats 
                          likes={post.postLikes} 
                          commentsCount={post.commentsCount} 
                        />
                      </>
                    ) : (
                      <div className="bg-black-200 flex h-full w-full items-center justify-center text-lg font-semibold text-gray-400">
                        Video Not Found
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: User Info, Post Text, Comments */}
                <div className="flex h-full max-w-[480px] flex-col p-2 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {post.postUsername || post.user.name}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-800">
                    {post.postDesc || post.text}
                  </div>
                  <div className="my-2 border-t border-gray-200" />
                  <div className="mb-1 text-sm font-bold">
                    Top Comments
                  </div>
                  <div className="hide-scrollbar flex max-h-40 flex-col gap-1 overflow-y-auto pr-1">
                    {post.topComments && post.topComments.length > 0 ? (
                      <CommentList 
                        comments={post.topComments.map(comment => ({
                          user: { name: comment.username, avatar: "/placeholderpfp.jpg" },
                          text: comment.commentText,
                          likes: comment.likes
                        }))} 
                        showUsernamePrefix={true}
                      />
                    ) : (
                      <div className="text-sm text-gray-400">
                        No comments yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </ScrollContainer>
      </div>

      {/* Other Brand Related Comments */}
      <div className="bg-white">
        <div className="max-w-8xl mx-auto px-4 py-8 sm:px-2 lg:px-4">
          <div className="mb-4">
            <h1 className="mb-0 ml-0 text-left text-[20px] font-bold text-black">
              Other brand related comments
            </h1>
          </div>
          <div className="grid max-h-60 grid-cols-1 gap-10 overflow-y-scroll [-ms-overflow-style:none] [scrollbar-width:none] md:grid-cols-2 [&::-webkit-scrollbar]:hidden">
            {brandComments.length === 0 ? (
              <div className="text-gray-500">
                No comments available for this brand.
              </div>
            ) : (
              brandComments.map((comment, idx) => (
                <CommentCard
                  key={comment.commentId || idx}
                  comment={{
                    username: comment.username,
                    commentText: comment.commentText,
                    likes: comment.likes,
                    hasHeart: true,
                    viewSource: comment.sourceUrl,
                  }}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BrandDetailView;
